import type {
  BackendExperiment,
  StoredExperimentData,
} from '@core/abTesting/backend/types'
import {
  decodeABTestCookiePayload,
  encodeABTestCookiePayload,
  FW_ABT_COOKIE_MAX_ENCODED_LENGTH,
} from '@core/abTesting/cookiePayload'
import { getCookie } from '@core/storage'
import { getItem, setItem } from '@core/storage/localStorage'
import { StorageKeyPerformanceEnum } from '@core/storage/types'
import { removeCookieAndFallback } from '@core/tracking/funnelTracking'
import { setCookieWithRootDomain } from '@core/tracking/funnelTracking/cookies/setCookieWithRootDomain'

import { logABTestingInfo } from '../logging'

/**
 * Cookie expiration time in days
 */
const COOKIE_EXPIRATION_DAYS = 30

/**
 * Cache duration in milliseconds (10 minutes)
 */
const CACHE_DURATION_MS = 10 * 60 * 1000

interface ParsedExperimentData {
  experiments: BackendExperiment[]
  timestamp: number
}

function getFwAbtCookieDomain(): string | undefined {
  return typeof window !== 'undefined' ? window.location.hostname : undefined
}

/** Avoid dynamic date-fns chunk loads (ChunkLoadError when fwn.js is remapped). */
function addDays(date: Date, days: number): Date {
  const result = new Date(date.getTime())
  result.setDate(result.getDate() + days)
  return result
}

/**
 * Parses cookie/localStorage payload into experiments + timestamp.
 * New format stores `experiments` as a JSON string; legacy format kept the array.
 */
function parseBackendExperimentData(
  raw: string | undefined | null,
): ParsedExperimentData | null {
  if (!raw || typeof raw !== 'string') {
    return null
  }

  try {
    const data = JSON.parse(decodeABTestCookiePayload(raw)) as {
      experiments?: unknown
      timestamp?: number
    }

    let experiments: BackendExperiment[] | null = null

    if (typeof data.experiments === 'string') {
      const parsed = JSON.parse(data.experiments)
      if (Array.isArray(parsed)) {
        experiments = parsed
      }
    } else if (Array.isArray(data.experiments)) {
      // Legacy cookies wrote experiments as a raw array.
      experiments = data.experiments
    }

    if (!experiments || typeof data.timestamp !== 'number') {
      return null
    }

    return {
      experiments,
      timestamp: data.timestamp,
    }
  } catch {
    return null
  }
}

/**
 * Reads backend experiment cache from cookie or localStorage.
 * Prefers either source that has the backend `{ experiments, timestamp }` shape
 * so a legacy PLG `{ tests }` cookie cannot hide a valid localStorage cache.
 */
function readStoredExperimentData(): ParsedExperimentData | null {
  const fromCookie = parseBackendExperimentData(
    getCookie(StorageKeyPerformanceEnum.fw_abt),
  )
  if (fromCookie) {
    return fromCookie
  }

  const fromLocalStorage = parseBackendExperimentData(
    getItem<string>(StorageKeyPerformanceEnum.fw_abt),
  )
  if (fromLocalStorage) {
    return fromLocalStorage
  }

  return null
}

/**
 * Sets experiments data in the fw_abt cookie on the root domain
 * (same path as fw_bid / fw_uid). Always mirrors payload to localStorage so
 * empty/valid experiment caches survive when the cookie cannot stick.
 *
 * `experiments` is stored as a JSON string so an empty list is `"[]"` rather
 * than a raw `[]` array inside the cookie JSON.
 *
 * Cookie values are percent-encoded so `{`/`[` never reach Cookie headers
 * (Feign URI-template parsing - CS-15608: I'm looking at you Academy!). localStorage keeps the raw JSON payload.
 */
export async function setExperimentsCookie(
  experiments: BackendExperiment[],
): Promise<void> {
  const expirationDate = addDays(new Date(), COOKIE_EXPIRATION_DAYS)
  const data: StoredExperimentData = {
    experiments: JSON.stringify(experiments),
    timestamp: Date.now(),
  }
  const payload = JSON.stringify(data)
  const encoded = encodeABTestCookiePayload(payload)

  // setCookieWithRootDomain clears localStorage for this key when probing domain
  // ownership — write the cookie first, then mirror to localStorage.
  if (encoded.length > FW_ABT_COOKIE_MAX_ENCODED_LENGTH) {
    logABTestingInfo(
      `fw_abt encoded payload exceeds ${FW_ABT_COOKIE_MAX_ENCODED_LENGTH} bytes; skipping cookie write and using localStorage only`,
    )
  } else {
    setCookieWithRootDomain(StorageKeyPerformanceEnum.fw_abt, encoded, {
      expirationDate,
      domain: getFwAbtCookieDomain(),
    })
  }
  setItem(StorageKeyPerformanceEnum.fw_abt, payload, expirationDate)

  if (
    encoded.length <= FW_ABT_COOKIE_MAX_ENCODED_LENGTH &&
    !getCookie(StorageKeyPerformanceEnum.fw_abt)
  ) {
    logABTestingInfo(
      'fw_abt cookie not readable after write; relying on localStorage fallback',
    )
  }

  logABTestingInfo(
    `Backend A/B Tests stored: ${experiments.length} experiments`,
  )
}

/**
 * Gets experiments data from the fw_abt cookie / localStorage fallback
 */
export function getStoredExperiments(): BackendExperiment[] | null {
  return readStoredExperimentData()?.experiments ?? null
}

/**
 * Checks if stored experiments are still valid (not expired)
 */
export function areStoredExperimentsValid(): boolean {
  const data = readStoredExperimentData()

  if (!data?.timestamp) {
    return false
  }

  const age = Date.now() - data.timestamp
  return age < CACHE_DURATION_MS
}

/**
 * Clears the experiments cookie and localStorage fallback
 */
export function clearExperimentsCookie(): void {
  removeCookieAndFallback(StorageKeyPerformanceEnum.fw_abt)
  logABTestingInfo('Backend A/B Tests cookie cleared')
}
